import React, { useState, useEffect } from 'react';
import { adminClient } from '@/api/adminClient';
import type { Room } from '@/types';

export default function RoomsPage() {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // New room form
  const [showNewForm, setShowNewForm] = useState(false);
  const [newRoom, setNewRoom] = useState({ id: '', name: '', description: '' });
  const [isCreating, setIsCreating] = useState(false);

  // Edit room
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [editForm, setEditForm] = useState({ name: '', description: '' });
  const [isUpdating, setIsUpdating] = useState(false);

  // Delete confirmation
  const [deletingRoomId, setDeletingRoomId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadRooms();
  }, []);

  const loadRooms = async () => {
    try {
      setLoading(true);
      const data = await adminClient.getRooms();
      setRooms(data);
    } catch (err: any) {
      setError('Failed to load rooms');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRoom.id || !newRoom.name) {
      setError('Room ID and name are required');
      return;
    }

    try {
      setIsCreating(true);
      setError('');
      await adminClient.createRoom(newRoom);
      setSuccess('Room created successfully');
      setNewRoom({ id: '', name: '', description: '' });
      setShowNewForm(false);
      await loadRooms();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to create room');
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingRoom) return;

    try {
      setIsUpdating(true);
      setError('');
      await adminClient.updateRoom(editingRoom.id, editForm);
      setSuccess('Room updated successfully');
      setEditingRoom(null);
      await loadRooms();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update room');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteRoom = async () => {
    if (!deletingRoomId) return;

    try {
      setIsDeleting(true);
      setError('');
      await adminClient.deleteRoom(deletingRoomId);
      setSuccess('Room deleted successfully');
      setDeletingRoomId(null);
      await loadRooms();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to delete room');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleActive = async (room: Room) => {
    try {
      setError('');
      await adminClient.toggleRoom(room.id, !room.isActive);
      await loadRooms();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to toggle room');
    }
  };

  const startEdit = (room: Room) => {
    setEditingRoom(room);
    setEditForm({ name: room.name, description: room.description });
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading rooms...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Room Management</h1>
        <button
          onClick={() => setShowNewForm(true)}
          style={styles.addButton}
        >
          + Add Room
        </button>
      </div>

      {error && <div style={styles.errorAlert}>{error}</div>}
      {success && <div style={styles.successAlert}>{success}</div>}

      {/* New Room Form */}
      {showNewForm && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2 style={styles.modalTitle}>Create New Room</h2>
            <form onSubmit={handleCreateRoom}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Room ID</label>
                <input
                  type="text"
                  value={newRoom.id}
                  onChange={(e) => setNewRoom({ ...newRoom, id: e.target.value })}
                  style={styles.input}
                  placeholder="e.g., 7"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Room Name</label>
                <input
                  type="text"
                  value={newRoom.name}
                  onChange={(e) => setNewRoom({ ...newRoom, name: e.target.value })}
                  style={styles.input}
                  placeholder="e.g., Room G - Hall 1"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <input
                  type="text"
                  value={newRoom.description}
                  onChange={(e) => setNewRoom({ ...newRoom, description: e.target.value })}
                  style={styles.input}
                  placeholder="e.g., Special sessions"
                />
              </div>
              <div style={styles.modalActions}>
                <button
                  type="button"
                  onClick={() => setShowNewForm(false)}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  style={styles.submitButton}
                >
                  {isCreating ? 'Creating...' : 'Create Room'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Room Modal */}
      {editingRoom && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2 style={styles.modalTitle}>Edit Room {editingRoom.id}</h2>
            <form onSubmit={handleUpdateRoom}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Room Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <input
                  type="text"
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  style={styles.input}
                />
              </div>
              <div style={styles.modalActions}>
                <button
                  type="button"
                  onClick={() => setEditingRoom(null)}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isUpdating}
                  style={styles.submitButton}
                >
                  {isUpdating ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingRoomId && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2 style={styles.modalTitle}>Delete Room?</h2>
            <p style={styles.modalText}>
              Are you sure you want to delete Room {deletingRoomId}? This action cannot be undone.
            </p>
            <div style={styles.modalActions}>
              <button
                onClick={() => setDeletingRoomId(null)}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteRoom}
                disabled={isDeleting}
                style={styles.deleteButton}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rooms List */}
      <div style={styles.roomsList}>
        {rooms.length === 0 ? (
          <div style={styles.emptyState}>No rooms configured</div>
        ) : (
          rooms.map((room) => (
            <div key={room.id} style={styles.roomCard}>
              <div style={styles.roomHeader}>
                <div style={styles.roomInfo}>
                  <span style={styles.roomId}>Room {room.id}</span>
                  <h3 style={styles.roomName}>{room.name}</h3>
                  <p style={styles.roomDescription}>{room.description}</p>
                </div>
                <div style={styles.roomStatus}>
                  <button
                    onClick={() => handleToggleActive(room)}
                    style={{
                      ...styles.statusBadge,
                      backgroundColor: room.isActive ? '#c6f6d5' : '#fed7d7',
                      color: room.isActive ? '#22543d' : '#c53030',
                    }}
                  >
                    {room.isActive ? 'Active' : 'Inactive'}
                  </button>
                </div>
              </div>
              <div style={styles.roomMeta}>
                <span>Participants: {room.participantCount}</span>
                <span>Updated: {new Date(room.updatedAt).toLocaleString()}</span>
              </div>
              <div style={styles.roomActions}>
                <button
                  onClick={() => startEdit(room)}
                  style={styles.editButton}
                >
                  Edit
                </button>
                <button
                  onClick={() => setDeletingRoomId(room.id)}
                  style={styles.deleteActionButton}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#2d3748',
    margin: 0,
  },
  addButton: {
    padding: '10px 20px',
    backgroundColor: '#4299e1',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: '#718096',
  },
  errorAlert: {
    padding: '12px',
    backgroundColor: '#fed7d7',
    border: '1px solid #fc8181',
    borderRadius: '6px',
    color: '#c53030',
    marginBottom: '16px',
  },
  successAlert: {
    padding: '12px',
    backgroundColor: '#c6f6d5',
    border: '1px solid #68d391',
    borderRadius: '6px',
    color: '#22543d',
    marginBottom: '16px',
  },
  roomsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: '#a0aec0',
    backgroundColor: '#f7fafc',
    borderRadius: '8px',
  },
  roomCard: {
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '20px',
  },
  roomHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
  },
  roomInfo: {
    flex: 1,
  },
  roomId: {
    fontSize: '12px',
    color: '#718096',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  roomName: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2d3748',
    margin: '4px 0',
  },
  roomDescription: {
    fontSize: '14px',
    color: '#718096',
    margin: 0,
  },
  roomStatus: {},
  statusBadge: {
    padding: '6px 12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500',
    border: 'none',
    cursor: 'pointer',
  },
  roomMeta: {
    display: 'flex',
    gap: '24px',
    fontSize: '13px',
    color: '#a0aec0',
    marginBottom: '16px',
  },
  roomActions: {
    display: 'flex',
    gap: '8px',
    borderTop: '1px solid #e2e8f0',
    paddingTop: '16px',
  },
  editButton: {
    padding: '8px 16px',
    backgroundColor: '#edf2f7',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    fontSize: '14px',
    color: '#4a5568',
    cursor: 'pointer',
  },
  deleteActionButton: {
    padding: '8px 16px',
    backgroundColor: '#fff5f5',
    border: '1px solid #feb2b2',
    borderRadius: '4px',
    fontSize: '14px',
    color: '#c53030',
    cursor: 'pointer',
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '24px',
    width: '100%',
    maxWidth: '400px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2d3748',
    marginBottom: '16px',
  },
  modalText: {
    fontSize: '14px',
    color: '#718096',
    marginBottom: '20px',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#4a5568',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '20px',
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#edf2f7',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    color: '#4a5568',
    cursor: 'pointer',
  },
  submitButton: {
    padding: '10px 20px',
    backgroundColor: '#4299e1',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    color: 'white',
    cursor: 'pointer',
  },
  deleteButton: {
    padding: '10px 20px',
    backgroundColor: '#e53e3e',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    color: 'white',
    cursor: 'pointer',
  },
};
